import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import Button from '@orn/shared-web/components/Button/Button';
import * as React from 'react';

import { BillingPortalFlow } from '../../../__generated__/globalTypes';
import { useCreateBillingPortalSession } from '../../../hooks/mutations/useCreateBillingPortalSession';
import { useFeatureGate } from '../../../hooks/useFeatureGates';

/**
 * Entry points into the Stripe customer portal: a "Manage membership" action
 * opening the portal home (plan switch, invoices, cancel) and a quieter
 * "Cancel membership" link deep-linking straight into the cancellation flow.
 * Mints the session URL through `createBillingPortalSession` on click and
 * hands the whole tab to Stripe; renders nothing while the billing feature
 * gate is closed. Stays disabled once a session is minted — the browser is
 * already navigating away.
 */
const ManageMembershipButton = () => {
  const billingEnabled = useFeatureGate(FeatureGate.BillingCheckout);
  const [createBillingPortalSession, { error }] = useCreateBillingPortalSession();
  const [pendingFlow, setPendingFlow] = React.useState<BillingPortalFlow | null>(null);

  if (!billingEnabled) {
    return null;
  }

  const openPortal = async (flow: BillingPortalFlow) => {
    setPendingFlow(flow);
    try {
      const { data } = await createBillingPortalSession({ variables: { input: { flow } } });
      const url = data?.createBillingPortalSession.url;
      if (url) {
        window.location.assign(url);
        return;
      }
    } catch {
      // Surfaced through the mutation's error state below.
    }
    setPendingFlow(null);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-4">
        <Button
          color="secondary"
          size="md"
          isLoading={pendingFlow === BillingPortalFlow.Manage}
          isDisabled={pendingFlow !== null}
          onClick={() => void openPortal(BillingPortalFlow.Manage)}
        >
          Manage membership
        </Button>
        <Button
          color="link-gray"
          size="md"
          isLoading={pendingFlow === BillingPortalFlow.Cancel}
          isDisabled={pendingFlow !== null}
          onClick={() => void openPortal(BillingPortalFlow.Cancel)}
        >
          Cancel membership
        </Button>
      </div>
      {pendingFlow === null && error !== undefined && (
        <p className="text-error-primary text-sm">Couldn't open billing management. Please try again.</p>
      )}
    </div>
  );
};

ManageMembershipButton.displayName = 'ManageMembershipButton';

export default ManageMembershipButton;
